import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Printer, Receipt, CheckCircle2, ShieldCheck } from 'lucide-react';
import { formatDate, formatDateTime } from '@/utils/dateTime';

export const ReceiptPrintModal = ({
  open,
  onOpenChange,
  payment,
  invoice,
  patient,
  clinicName,
  hospitalName,
}) => {
  const printRef = React.useRef();

  React.useEffect(() => {
    if (open) {
      document.body.classList.add('modal-printing');
    } else {
      document.body.classList.remove('modal-printing');
    }
    return () => {
      document.body.classList.remove('modal-printing');
    };
  }, [open]);

  if (!payment) return null;

  const handlePrint = () => {
    window.print();
  };

  const receiptNumber = payment.receiptNumber || `RCT-${new Date().getFullYear()}-001`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 border-0 bg-slate-100">
        <DialogHeader className="p-4 bg-slate-900 text-white rounded-t-xl flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="text-base font-bold text-white flex items-center gap-2">
              <Receipt className="h-4 w-4 text-emerald-400" />
              Payment Receipt — {receiptNumber}
            </DialogTitle>
            <p className="text-[11px] text-slate-400">Official proof of payment transaction</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Print Receipt
            </Button>
          </div>
        </DialogHeader>

        {/* Printable Receipt Paper */}
        <div
          ref={printRef}
          id="printable-receipt"
          className="p-8 bg-white mx-auto my-4 max-w-xl w-full rounded-xl shadow-md border border-slate-200 text-slate-800 space-y-6 print:shadow-none print:border-none print:m-0 print:p-6 print:max-w-full"
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-emerald-700 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-700 text-white flex items-center justify-center font-bold text-sm">
                  PF
                </div>
                <h2 className="text-lg font-heading font-extrabold text-slate-950 uppercase tracking-tight">
                  {hospitalName || 'PulseFlow Memorial Hospital'}
                </h2>
              </div>
              <p className="text-[11px] text-slate-500">
                {clinicName || 'General OPD Clinic'} • Cashier Counter #1
              </p>
            </div>
            <div className="text-right space-y-1">
              <span className="text-xs uppercase font-extrabold tracking-wider text-emerald-800 block">
                PAYMENT RECEIPT
              </span>
              <p className="text-xs font-mono font-bold text-slate-900">{receiptNumber}</p>
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] font-bold">
                ✓ RECEIVED
              </Badge>
            </div>
          </div>

          {/* Receipt Details Box */}
          <div className="bg-emerald-50/50 border border-emerald-200 p-4 rounded-xl space-y-3">
            <div className="flex items-center justify-between border-b border-emerald-100 pb-2">
              <span className="text-xs text-slate-600">Amount Received:</span>
              <span className="text-2xl font-mono font-black text-emerald-700">
                ₹{Number(payment.amount).toFixed(2)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <p className="text-slate-500 text-[10px] uppercase font-semibold">Payment Mode</p>
                <p className="font-bold text-slate-900">{payment.paymentMode || 'CASH'}</p>
              </div>
              <div>
                <p className="text-slate-500 text-[10px] uppercase font-semibold">Transaction Date/Time</p>
                <p className="font-medium text-slate-800">{formatDateTime(payment.paymentDate || new Date())}</p>
              </div>
              {payment.referenceNumber && (
                <div className="col-span-2">
                  <p className="text-slate-500 text-[10px] uppercase font-semibold">Reference / UTR Number</p>
                  <p className="font-mono text-slate-900 font-bold">{payment.referenceNumber}</p>
                </div>
              )}
            </div>
          </div>

          {/* Patient & Invoice Linkage */}
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-500">Patient Name:</span>
              <strong className="text-slate-900">
                {patient?.firstName ? `${patient.firstName} ${patient.lastName}` : invoice?.patientName || 'Patient'}
              </strong>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Patient Identifier:</span>
              <span className="font-mono text-slate-700">
                {patient?.patientCode || invoice?.patientCode || invoice?.patientId || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Against Invoice:</span>
              <span className="font-mono font-semibold text-slate-800">
                {payment.invoiceNumber || invoice?.invoiceNumber || 'N/A'}
              </span>
            </div>
            {invoice && (
              <>
                <div className="flex justify-between border-t border-slate-200 pt-1.5">
                  <span className="text-slate-500">Total Invoice Amount:</span>
                  <span className="font-mono text-slate-800">₹{Number(invoice.totalAmount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cumulative Paid:</span>
                  <span className="font-mono text-emerald-700 font-semibold">
                    ₹{Number(invoice.paidAmount).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between font-bold">
                  <span className="text-slate-700">Remaining Balance:</span>
                  <span className="font-mono text-red-600">
                    ₹{Number(invoice.balanceAmount).toFixed(2)}
                  </span>
                </div>
              </>
            )}
          </div>

          {payment.notes && (
            <div className="text-xs text-slate-500 italic bg-white p-2 rounded border border-slate-100">
              Note: {payment.notes}
            </div>
          )}

          {/* Footer & Signature */}
          <div className="pt-6 border-t border-slate-200 flex items-end justify-between text-[11px] text-slate-500">
            <div className="flex items-center gap-1.5 text-emerald-700 font-medium">
              <ShieldCheck className="h-4 w-4" />
              Verified Electronic Transaction
            </div>
            <div className="text-center space-y-1">
              <div className="w-28 border-b border-slate-400 pb-6" />
              <span className="text-[10px] font-semibold text-slate-700 block">Received By</span>
            </div>
          </div>
        </div>

        <DialogFooter className="p-4 bg-slate-50 border-t border-slate-200">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="text-xs">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReceiptPrintModal;

